import { useEffect, useState } from 'react'
import { BrowserRouter, Navigate, Outlet, Route, Routes, useOutletContext } from 'react-router-dom'
import {
  getCurrentAdmin,
  logoutAdmin
} from './api/auth'
import { ToastProvider } from './components/ToastProvider'
import { AudioPlayerProvider } from './features/audio/AudioPlayerProvider'
import { AdminLayout, type AdminOutletContext } from './layouts/AdminLayout'
import { AudioPage } from './pages/AudioPage'
import { ChannelPlaygroundPage } from './pages/ChannelPlaygroundPage'
import { ChannelsPage } from './pages/ChannelsPage'
import { PublicChannelPage } from './pages/PublicChannelPage'
import { PublicChannelsPage } from './pages/PublicChannelsPage'
import { LoginPage } from './pages/LoginPage'
import { LabelsPage } from './pages/LabelsPage'
import { ListeningSignalsPage } from './pages/ListeningSignalsPage'
import { ToolsPage } from './pages/ToolsPage'
import { TrackTitleGeneratorPage } from './pages/TrackTitleGeneratorPage'
import { StylePromptGeneratorPage } from './pages/StylePromptGeneratorPage'
import { PromptLibraryPage } from './pages/PromptLibraryPage'
import { PromptGroupsPage } from './pages/PromptGroupsPage'
import { VisualsPage } from './pages/VisualsPage'
import type { AuthState } from './types/auth'
import styles from './App.module.css'

const AudioPlayerRoute = () => {
  const adminContext = useOutletContext<AdminOutletContext>()

  return (
    <AudioPlayerProvider>
      <Outlet context={adminContext} />
    </AudioPlayerProvider>
  )
}

type ProtectedAdminProps = {
  auth: AuthState
}

const ProtectedAdmin = ({ auth }: ProtectedAdminProps) => {
  if (auth.state === 'loading') {
    return <main className={styles.routeLoading}>Loading admin...</main>
  }

  if (auth.state !== 'authenticated') {
    return <Navigate replace to="/admin/login" />
  }

  return <Outlet />
}

type AdminShellProps = {
  auth: AuthState
  onLogout: () => Promise<void>
}

const AdminShell = ({ auth, onLogout }: AdminShellProps) => (
  auth.state === 'authenticated'
    ? <AdminLayout onLogout={onLogout} user={auth.user} />
    : <Outlet />
)

const App = () => {
  const [auth, setAuth] = useState<AuthState>({ state: 'loading' })

  useEffect(() => {
    const boot = async (): Promise<void> => {
      try {
        const user = await getCurrentAdmin()
        setAuth(user ? { state: 'authenticated', user } : { state: 'anonymous' })
      } catch (error) {
        setAuth({
          state: 'error',
          message: error instanceof Error ? error.message : 'Unknown error'
        })
      }
    }

    void boot()
  }, [])

  const handleLogout = async (): Promise<void> => {
    await logoutAdmin()
    setAuth({ state: 'anonymous' })
  }

  return (
    <BrowserRouter>
      <ToastProvider>
        <Routes>
          <Route index element={<PublicChannelsPage auth={auth} />} />
          <Route path="channels" element={<PublicChannelsPage auth={auth} />} />
          <Route path="channels/:channelSlug" element={<PublicChannelPage />} />
          <Route
            path="admin/login"
            element={
              auth.state === 'authenticated'
                ? <Navigate replace to="/admin/channels" />
                : (
                  <LoginPage
                    auth={auth}
                    onAuthenticated={setAuth}
                  />
                )
            }
          />
          <Route element={<ProtectedAdmin auth={auth} />} path="admin">
        <Route
          element={<AdminShell auth={auth} onLogout={handleLogout} />}
        >
          <Route index element={<Navigate replace to="/admin/channels" />} />
          <Route element={<ChannelsPage />} path="channels" />
          <Route
            element={<ChannelPlaygroundPage />}
            path="channels/:channelId/playground"
          />
          <Route
            element={<VisualsPage tab="library" />}
            path="visuals"
          />
          <Route
            element={<VisualsPage tab="groups" />}
            path="visuals/groups"
          />
          <Route element={<AudioPlayerRoute />}>
            <Route
              element={<AudioPage tab="tracks" />}
              path="audio"
            />
            <Route
              element={<AudioPage tab="groups" />}
              path="audio/groups"
            />
            <Route
              element={<AudioPage tab="assignments" />}
              path="audio/assignments"
            />
          </Route>
          <Route element={<LabelsPage />} path="labels" />
          <Route element={<AudioPlayerRoute />}>
            <Route element={<ListeningSignalsPage />} path="listening-signals" />
          </Route>
          <Route element={<ToolsPage />} path="tools" />
          <Route element={<TrackTitleGeneratorPage />} path="tools/track-name-ideas" />
          <Route element={<StylePromptGeneratorPage />} path="tools/suno-style-prompts" />
          <Route element={<PromptLibraryPage />} path="tools/prompt-management" />
          <Route element={<PromptGroupsPage />} path="tools/prompt-management/groups" />
          <Route path="*" element={<Navigate replace to="/admin/channels" />} />
        </Route>
          </Route>
          <Route path="*" element={<Navigate replace to="/" />} />
      </Routes>
      </ToastProvider>
    </BrowserRouter>
  )
}

export default App
